import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { PublicHeader } from '@/components/public/public-header';
import { api } from '@/api/http';
import { useQuery } from '@tanstack/react-query';

export const Route = createFileRoute('/register')({
  validateSearch: (s: Record<string, unknown>) => ({
    next: typeof s.next === 'string' ? s.next : undefined,
    ref: typeof s.ref === 'string' ? s.ref : undefined,
    email: typeof s.email === 'string' ? s.email : undefined,
  }),
  component: RegisterPage,
});

/**
 * SEC-CRIT-3 — pede ao backend uma URL de auth com state assinado HMAC.
 * Antes o frontend montava a URL manualmente com state em texto puro
 * (`?state=/onboarding/therapist`), permitindo CSRF/session-fixation.
 */
async function startGoogleAuth(role: 'CLIENT' | 'THERAPIST', next: string) {
  const { url } = await api.get('auth/google/start', {
    searchParams: { role, next },
  }).json<{ url: string }>();
  window.location.href = url;
}

/**
 * Fluxo "já tenho conta" — usa o mesmo endpoint de OAuth. O backend
 * reconhece o email existente e ignora o role passado, então é seguro
 * usar CLIENT como default aqui (não recria como cliente se já for
 * profissional).
 */
async function startGoogleLogin(next: string) {
  const { url } = await api.get('auth/google/start', {
    searchParams: { role: 'CLIENT', next },
  }).json<{ url: string }>();
  window.location.href = url;
}

function RegisterPage() {
  const search = useSearch({ from: '/register' }) as { next?: string; ref?: string; email?: string };

  // Onboarding obrigatório (cria ClientProfile), e propaga `next` por dentro do onboarding
  // pra retornar à tela de agendamento com o slot escolhido após o cadastro.
  const clientReturn = search.next
    ? `/onboarding/client?next=${encodeURIComponent(search.next)}`
    : '/onboarding/client';
  const therapistReturn = '/onboarding/therapist';
  const loginNext = search.next ?? '/dashboard';

  // Se Google não estiver configurado, esconde o botão do card superior
  // (o /login já cobre o fallback com OTP)
  const { data: providers } = useQuery({
    queryKey: ['auth-providers'],
    queryFn: () => api.get('auth/providers').json<{ google: boolean }>(),
  });
  const googleEnabled = providers?.google ?? false;

  return (
    <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 flex items-start justify-center p-6 md:p-12">
        <div className="w-full max-w-3xl">
          {/* Bloco superior: acesso rápido para quem já tem conta.
              Ajuda usuários existentes a não se perderem na tela de escolha
              de tipo. Fica em destaque visual com fundo diferenciado. */}
          <section className="bg-white rounded-2xl border-2 border-teal-200 shadow-sm p-5 md:p-6 mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
              <div className="flex-1">
                <h2 className="text-lg md:text-xl font-bold text-slate-800">Já tem conta?</h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  Entre pra acessar seu perfil e continuar de onde parou.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                {googleEnabled && (
                  <button
                    type="button"
                    onClick={() => startGoogleLogin(loginNext)}
                    className="inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white h-11 px-5 rounded-md font-semibold transition whitespace-nowrap"
                  >
                    <GoogleIcon />
                    Entrar com Google
                  </button>
                )}
                <Link
                  to="/login"
                  search={search.next ? { next: search.next } : undefined}
                  className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-teal-400 hover:bg-teal-50 text-slate-700 h-11 px-5 rounded-md font-semibold transition whitespace-nowrap"
                >
                  Entrar com e-mail
                </Link>
              </div>
            </div>
          </section>

          <div className="text-center mb-6">
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">Novo por aqui? Crie sua conta</h1>
            <p className="text-slate-500 mt-2">Escolha o tipo de conta abaixo</p>
          </div>

          {search.email && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 text-sm text-amber-900">
              Não encontramos uma conta para <strong>{search.email}</strong>. Escolha abaixo como deseja se cadastrar.
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="h-full flex flex-col bg-white rounded-2xl border border-slate-100 shadow-sm">
              <div className="p-6">
                <div className="size-12 rounded-xl bg-teal-100 flex items-center justify-center text-2xl mb-2">🧑</div>
                <h2 className="text-xl font-bold text-slate-800">Sou Cliente</h2>
                <p className="text-sm text-slate-500 mt-1">Quero encontrar um profissional e agendar sessões</p>
              </div>
              <div className="px-6 pb-6 flex flex-col flex-1 justify-between gap-4">
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>✓ Busca filtrada por especialidade e abordagem</li>
                  <li>✓ Agenda integrada com Google Calendar</li>
                  <li>✓ Sessões online ou presenciais</li>
                  <li>✓ Seus dados ficam protegidos e privados</li>
                </ul>
                <button
                  type="button"
                  onClick={() => startGoogleAuth('CLIENT', clientReturn)}
                  className="w-full inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white h-11 rounded-md font-medium transition"
                >
                  <GoogleIcon />
                  Criar conta com Google
                </button>
              </div>
            </div>

            <div className="h-full flex flex-col bg-white rounded-2xl border-2 border-amber-300 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl uppercase tracking-wider">
                🎁 Promo de adesão
              </div>
              <div className="p-6 pt-10">
                <div className="size-12 rounded-xl bg-indigo-100 flex items-center justify-center text-2xl mb-2">🧑‍⚕️</div>
                <h2 className="text-xl font-bold text-slate-800">Sou Profissional</h2>
                <p className="text-sm text-slate-500 mt-1">Quero oferecer meus serviços e gerenciar clientes</p>
              </div>
              <div className="px-6 pb-6 flex flex-col flex-1 justify-between gap-4">
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                  <p className="text-sm font-bold text-amber-900 leading-tight">
                    3 primeiros meses sem taxa da plataforma
                  </p>
                  <p className="text-xs text-amber-800 mt-1 leading-snug">
                    Cadastre-se agora e fique 90 dias com <strong>0% de comissão</strong> por sessão
                    — você recebe <strong>100% do valor</strong> que o cliente paga.
                  </p>
                </div>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>✓ Perfil profissional completo</li>
                  <li>✓ Gestão de agenda e pagamentos</li>
                  <li>✓ Ciclo de faturamento automático</li>
                  <li>✓ Sessões online via Google Meet</li>
                </ul>
                <button
                  type="button"
                  onClick={() => startGoogleAuth('THERAPIST', therapistReturn)}
                  className="w-full inline-flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white h-11 rounded-md font-medium transition"
                >
                  <GoogleIcon />
                  Criar conta com Google
                </button>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}
